/**
 * Shared types and constants for motion-based molecular rendering
 *
 * @package @alchemiesolns/svg-interactive
 */

import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';

/**
 * Spring physics configuration for atom dragging
 *
 * Lower stiffness = softer, more elastic feel
 * Higher damping = faster settling, less oscillation
 * Lower restDelta/restSpeed = more precise but slower settling
 */
export interface SpringConfig {
  /** Spring stiffness (100-1000 typical range) */
  stiffness: number;
  /** Damping coefficient (10-100 typical range) */
  damping: number;
  /** Stop animating when within this distance of target (0.001-0.1 typical). Default: 0.001 */
  restDelta?: number;
  /** Stop animating when velocity below this threshold (0.01-1 typical). Default: 0.01 */
  restSpeed?: number;
  /** When true, bypass spring animation entirely - connected atoms move instantly with dragged atom */
  rigid?: boolean;
}

/**
 * Standard spring configuration for settling atoms after drag ends
 *
 * These values provide fast, crisp settlement without overshoot:
 * - High stiffness (1000) for immediate response
 * - High damping (100) for quick stop without oscillation
 * - Loose rest thresholds (0.1 units, 1 unit/sec) to avoid infinite settling
 */
export const SETTLEMENT_SPRING_CONFIG: SpringConfig = {
  stiffness: 1000,
  damping: 100,
  restDelta: 0.1,
  restSpeed: 1,
} as const;

/**
 * Default spring configuration for user-controlled dragging
 *
 * These values provide natural, responsive feel:
 * - Medium stiffness (300) for smooth following
 * - Medium damping (20) for slight trailing effect
 * - Tight rest thresholds for precise final positioning
 */
export const DEFAULT_DRAG_SPRING_CONFIG: SpringConfig = {
  stiffness: 300,
  damping: 20,
  restDelta: 0.001,
  restSpeed: 0.01,
} as const;

/**
 * Rigid spring configuration - no spring animation
 *
 * When rigid is true, connected atoms move instantly with the dragged atom
 * rather than following with spring physics. Use for precise, mechanical feel.
 */
export const RIGID_SPRING_CONFIG: SpringConfig = {
  stiffness: 1000,
  damping: 100,
  restDelta: 0.1,
  restSpeed: 1,
  rigid: true,
} as const;

/**
 * Slow spring configuration for layout animations
 *
 * Lower stiffness and damping create a slower, more visible animation
 * that's ideal for showing chemical transformations step-by-step.
 * - Low stiffness (50) for slow, smooth movement
 * - Low damping (10) for gentle settling
 * - Loose rest thresholds to avoid overly long animations
 */
export const SLOW_SPRING_CONFIG: SpringConfig = {
  stiffness: 50,
  damping: 10,
  restDelta: 0.1,
  restSpeed: 1,
} as const;

/**
 * Handlers returned by useMotionDragCoordination hook
 */
export interface MotionDragHandlers {
  /** Called when drag starts on any atom */
  handleDragStart: () => void;
  /** Called when drag ends, with optional atoms to settle */
  handleDragEnd: (atomsToSettle?: Array<{ id: string; x: number; y: number }>) => void;
  /** Whether any drag is currently active */
  isDragging: boolean;
}

// ============================================================================
// Natural Motion Configuration
// ============================================================================

import type { Point2D } from '../utils/dragConstraints';

/**
 * Configuration for natural motion drag behavior
 *
 * Controls how fragments respond to dragging with blended translation/rotation.
 * Configurable spring parameters allow tuning from rigid to natural feel.
 */
export interface NaturalMotionConfig {
  /**
   * Spring stiffness for rotation following (100-1000)
   * Lower = more elastic/loose feel with trailing
   * Higher = more rigid, atoms follow immediately
   * @default 200
   */
  rotationStiffness: number;

  /**
   * Spring damping for rotation (10-100)
   * Lower = more oscillation and bounciness
   * Higher = quicker settling with less overshoot
   * @default 15
   */
  rotationDamping: number;

  /**
   * Minimum distance from centroid before rotation kicks in (SVG units)
   * Prevents accidental rotation when dragging near center
   * @default 2.0
   */
  rotationDeadzone: number;

  /**
   * Whether rotation is enabled
   * When false, behaves like original rigid-body (pure translation)
   * @default true
   */
  enableRotation: boolean;

  /**
   * Disable orbit constraint for single-bond atoms
   *
   * When true, single-bond atoms (like terminal H atoms) move the entire
   * molecule as a rigid body instead of orbiting around their bonded neighbor.
   * Useful for apps where you want to drag from any atom to move the whole molecule.
   *
   * @default false
   */
  disableOrbitConstraint?: boolean;

  /**
   * Pre-filter which bonds count as skeletal for constraint detection and
   * orbit anchor resolution. Called for each bond neighbor — return false
   * to exclude it (e.g., exclude connective bonds to other groups).
   *
   * The standard hidden-hydrogen filter is always applied on top of this.
   *
   * Use case: in a grouped template editor, only count intra-group bonds
   * so group-terminal atoms orbit even when they have connective bonds.
   */
  skeletalBondFilter?: (workspace: MolecularWorkspace, atomId: string, neighborId: string) => boolean;
}

/**
 * Default natural motion configuration - balanced, natural feel
 *
 * Medium stiffness and damping provide a responsive but organic feel
 * where atoms trail slightly behind during fast movements.
 */
export const DEFAULT_NATURAL_MOTION_CONFIG: NaturalMotionConfig = {
  rotationStiffness: 200,
  rotationDamping: 15,
  rotationDeadzone: 2.0,
  enableRotation: true,
} as const;

/**
 * Rigid natural motion configuration - minimal trailing
 *
 * High stiffness and damping make atoms follow almost immediately.
 * Use when you want the natural motion blend behavior but with
 * tighter, more controlled movement.
 */
export const RIGID_NATURAL_MOTION_CONFIG: NaturalMotionConfig = {
  rotationStiffness: 800,
  rotationDamping: 50,
  rotationDeadzone: 5.0,
  enableRotation: true,
} as const;

/**
 * Captured state at drag start for natural motion calculations
 *
 * Stores all values needed to compute blended translation/rotation
 * during drag. Captured once at pointer-down, used throughout drag.
 */
export interface NaturalMotionBasis {
  /** Center of mass of the fragment (SVG coordinates) */
  centroid: Point2D;

  /** Maximum distance from centroid to any fragment atom */
  boundingRadius: number;

  /** Initial pointer position when drag started (SVG coordinates) */
  initialPointer: Point2D;

  /** Distance from centroid to initial pointer position */
  pointerDistanceFromCentroid: number;

  /** Angle from centroid to initial pointer (radians) */
  basisAngle: number;

  /**
   * Blend factor: 0 = pure translation, 1 = pure rotation
   * Calculated as (pointerDistanceFromCentroid / boundingRadius)²
   */
  blendFactor: number;

  /**
   * Connected atoms with their offsets FROM CENTROID
   * (not from dragged atom like original rigid-body)
   */
  atomOffsets: Array<{
    id: string;
    offsetX: number;
    offsetY: number;
  }>;

  /** The ID of the atom being directly dragged */
  draggedAtomId: string;
}

// ============================================================================
// Anchor Orbit Configuration
// ============================================================================

/**
 * Configuration for anchor orbit drag behavior
 *
 * When enabled and exactly one atom is selected, that atom becomes the "anchor".
 * Dragging another atom in the same fragment causes that side to orbit around
 * the anchor point.
 */
export interface AnchorOrbitConfig {
  /** Whether anchor orbit is enabled */
  enabled: boolean;
}

/**
 * Default anchor orbit configuration - disabled
 *
 * Anchor orbit must be explicitly enabled by the app.
 */
export const DEFAULT_ANCHOR_ORBIT_CONFIG: AnchorOrbitConfig = {
  enabled: false,
} as const;
